import PropTypes from "prop-types";
import React from "react";

import { useScreenSize } from "@swa-ui/browser";
import { Button, Icon, Text } from "@swa-ui/core";
import { Currency } from "@swa-ui/currency";
import i18n from "@swa-ui/locale";
import { classNames } from "@swa-ui/string";

import * as flightStatusType from "../defines/flightStatusType";
import styles from "./FareOption.module.scss";

const DEFAULT_DISABLED_FARE_OPTION_I18N_KEY = "FareOption__UNAVAILABLE_LABEL";
const SOLD_OUT_STATUSES = [flightStatusType.ALL_SEATS_SOLD, flightStatusType.SOLD_OUT];

/**
 * A component that displays a selectable fare option with pricing information.
 * Renders a compact collapsible variant on small screens and full details on larger screens.
 */
export const FareOption = (props) => {
  const {
    accrualPoints,
    availabilityStatus,
    amount,
    className,
    currencyCode,
    onOptionSelect,
    seatsLeft,
    selected,
    taxesAndFees,
  } = props;
  const { screenSize } = useScreenSize();
  const isSmallScreen = screenSize === "small";
  const disabledFareOption = availabilityStatus !== flightStatusType.AVAILABLE;
  const roundingType = parseFloat(amount) > 0 ? "up" : "down";

  return isSmallScreen ? renderCompactVariant() : renderFullVariant();

  function renderCompactVariant() {
    return (
      <Button {...getCompactButtonProps()}>
        <div className={styles.compactContent}>
          <Text color="var(--fare-option-text)" size="xlarge" textType="details">
            {i18n("FareOption__STARTING_AT_LABEL")}
          </Text>
          <Currency {...getCompactFareAmountProps()} />
          <Icon {...getCompactIconProps()} />
        </div>
      </Button>
    );
  }

  function renderFullVariant() {
    return <Button {...getFullVariantButtonProps()}>{renderFareOption()}</Button>;
  }

  function renderFareOption() {
    return disabledFareOption ? (
      <Text color="var(--fare-option-unavailable-text-color)" size="xlarge" textType="details">
        {i18n(getDisabledFareOptionI18nKey())}
      </Text>
    ) : (
      <div className={styles.fareOptionDetails}>
        <Currency {...getFareAmountProps()} />
        {currencyCode === "POINTS" ? (
          <>
            <Currency {...getTaxesAndFeesProps()} />
            {renderSeatsLeft()}
          </>
        ) : (
          <>
            {renderSeatsLeft()}
            {renderPointsAccrualOnHover()}
          </>
        )}
      </div>
    );
  }

  function renderSeatsLeft() {
    return (
      <Text {...getSeatsLeftProps()}>
        {i18n("FareOption__SEATS_LEFT_LABEL", { seatsLeft: seatsLeft ?? "0" })}
      </Text>
    );
  }

  function renderPointsAccrualOnHover() {
    return (
      <div className={classNames(styles.accrual, { [styles.hidden]: !accrualPoints })}>
        {accrualPoints && (
          <>
            <Text size="small">{i18n("FareOption__ACCRUAL_LABEL")}</Text>
            <Currency {...getPointsAccrualOnHoverProps()} />
          </>
        )}
      </div>
    );
  }

  function getCompactButtonProps() {
    return {
      className: classNames(className, styles.compactContainer),
      disabled: disabledFareOption,
      onClick: onOptionSelect,
      styleType: "no-style",
    };
  }

  function getCompactFareAmountProps() {
    return {
      amount,
      className: styles.compactFareAmount,
      currencyCode,
      roundingType,
      styleType: "primary",
      superScriptSymbol: true,
      textProps: {
        bold: true,
        color: "fare-option-currency",
        size: "xlarge",
        textType: "body",
      },
    };
  }

  function getCompactIconProps() {
    return {
      color: "fare-option-currency",
      custom: { type: selected ? "inactive" : "active" },
      name: "ArrowThin",
      role: "presentation",
      size: "size20",
    };
  }

  function getFullVariantButtonProps() {
    return {
      className: classNames(className, {
        [styles.activeContainer]: !disabledFareOption,
      }),
      disabled: disabledFareOption,
      emphasis: false,
      onClick: onOptionSelect,
      size: "minimal",
      styleType: selected ? "tertiary" : "secondary",
    };
  }

  function getFareAmountProps() {
    const color = selected
      ? "var(--fare-option-selected-text-color)"
      : "var(--fare-option-amount-color)";

    return {
      amount,
      currencyCode,
      roundingType,
      superScriptSymbol: true,
      textProps: {
        bold: true,
        color,
        size: "xsmall",
        textType: "display",
      },
    };
  }

  function getTaxesAndFeesProps() {
    const { currencyCode: taxesAndFeesCurrencyCode = "USD", value = "0" } = taxesAndFees ?? {};
    const color = selected
      ? "fare-option-selected-taxes-and-fees-color"
      : "fare-option-taxes-and-fees-color";

    return {
      amount: value,
      currencyCode: taxesAndFeesCurrencyCode,
      showSignPrefix: true,
      superScriptSymbol: false,
      textProps: {
        className: classNames(styles.currencyCentered, { [styles.hidden]: !taxesAndFees }),
        color,
      },
    };
  }

  function getSeatsLeftProps() {
    return {
      className: classNames({ [styles.hidden]: !seatsLeft }),
      color: "fare-option-seats-left-label",
      italics: true,
      size: "small",
    };
  }

  function getPointsAccrualOnHoverProps() {
    return {
      amount: accrualPoints,
      currencyCode: "POINTS",
      superScriptSymbol: false,
      textProps: {
        size: "small",
      },
    };
  }

  function getDisabledFareOptionI18nKey() {
    let key = DEFAULT_DISABLED_FARE_OPTION_I18N_KEY;

    if (SOLD_OUT_STATUSES.includes(availabilityStatus)) {
      key = "FareOption__SOLD_OUT_LABEL";
    } else if (availabilityStatus === flightStatusType.INVALID_WITH_DEPART_DATE) {
      key = "FareOption__INVALID_WITH_DEPART_DATE_LABEL";
    } else if (availabilityStatus === flightStatusType.INVALID_WITH_RETURN_DATE) {
      key = "FareOption__INVALID_WITH_RETURN_DATE_LABEL";
    }

    return key;
  }
};

FareOption.propTypes = {
  /** Points that will be accrued for this fare option */
  accrualPoints: PropTypes.string,

  /** The fare amount */
  amount: PropTypes.string,

  /** Field that describes the availability status of the flight. */
  availabilityStatus: PropTypes.oneOf(Object.values(flightStatusType)).isRequired,

  /** Additional class name to apply to the component */
  className: PropTypes.string,

  /** Currency code for the fare amount (e.g., 'USD') */
  currencyCode: PropTypes.string,

  /** Function called when the fare option is selected */
  onOptionSelect: PropTypes.func,

  /** Number of seats left at this fare price */
  seatsLeft: PropTypes.number,

  /** Indicates if this fare option is currently selected */
  selected: PropTypes.bool,

  /** Taxes and fees information for this fare */
  taxesAndFees: PropTypes.shape({
    currencyCode: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
  }),
};
